import { callFunctionWithResilience, invokeFireAndForget, type ResilienceOptions } from '@/utils/fetchWrapper';

/**
 * Request deduplication map.
 * Key = functionName + JSON.stringify(body)
 * Prevents duplicate in-flight calls from racing components / re-renders.
 */
const activeRequests = new Map<string, Promise<any>>();

/**
 * Smart memory cache with stale-while-revalidate.
 * Key = functionName + JSON.stringify(body)
 */
interface CacheEntry {
  data: any;
  expires: number;    // fresh until
  staleUntil: number; // SWR window — serve stale, revalidate in background
}
const cache = new Map<string, CacheEntry>();

/**
 * TTL configuration per function.
 * Functions not listed here are NOT cached.
 * Payment endpoints are explicitly excluded.
 */
const CACHE_TTL: Record<string, { freshMs: number; staleMs: number }> = {
  // NOTE: `internal-hotelpage` is intentionally NOT in this map.
  // Rates / book_hash / allotment / cancellation / payment_options change
  // by the second on the supplier side, so every visit goes back to the
  // edge function. Static enrichment (name, images, amenities) is cached
  // upstream by the edge function itself, not by this client.
  // The hotel-details page uses two complementary mechanisms instead:
  //   - `peekCache` returns null here (no TTL → no in-memory copy), so
  //   - the page warms back-nav with React state preserved by SPA caching,
  //     and `forceFresh: true` is passed on every Confirm/Book click to
  //     guarantee the rates the user clicks are the rates ETG sees.
  'liteapi-reviews':       { freshMs: 60_000, staleMs: 60_000 },
  'internal-reviews':      { freshMs: 60_000, staleMs: 60_000 },
  'offline-autocomplete':  { freshMs: 20_000, staleMs: 20_000 },
  'exora-search':          { freshMs: 20_000, staleMs: 20_000 },
  'ratehawk-multicomplete': { freshMs: 20_000, staleMs: 20_000 },
};

/** Payment / mutation endpoints — never cached */
const NEVER_CACHE = new Set([
  'stripe-create-payment-intent',
  'stripe-create-checkout',
  'ratehawk-prebook',
  'ratehawk-book',
]);

function getCacheTTL(fnName: string): { freshMs: number; staleMs: number } | null {
  if (NEVER_CACHE.has(fnName) || fnName.startsWith('stripe-')) return null;
  return CACHE_TTL[fnName] ?? null;
}

function dedupeKey(fnName: string, body: any): string {
  try {
    return fnName + '::' + JSON.stringify(body ?? {});
  } catch {
    return '';
  }
}

/**
 * Centralized, resilient API client for all Supabase Edge Function calls.
 *
 * Features:
 * - Smart memory cache with per-function TTL
 * - Stale-while-revalidate (Airbnb technique)
 * - 401 auto-recovery (session refresh + retry)
 * - Configurable timeout (default 15s)
 * - Automatic retries with exponential backoff
 * - Circuit breaker per function
 * - Request deduplication (identical in-flight calls share one Promise)
 * - Null-data guard
 * - Prefetch support for instant navigation
 */
export const apiClient = {
  /**
   * Call an edge function with full resilience + smart caching.
   * Duplicate in-flight calls (same function + body) are deduplicated.
   */
  async invoke<T = any>(
    functionName: string,
    body: any,
    options?: ResilienceOptions & { forceFresh?: boolean }
  ): Promise<{ data?: T; error?: Error; userMessage?: string }> {
    const key = dedupeKey(functionName, body);
    const ttl = getCacheTTL(functionName);
    const now = Date.now();
    const forceFresh = !!options?.forceFresh;

    // ── Cache check ── (skipped when forceFresh is requested — used for rate-critical refreshes)
    if (!forceFresh && key && ttl) {
      const entry = cache.get(key);
      if (entry) {
        if (now < entry.expires) {
          // Fresh cache hit — return immediately
          return { data: entry.data as T };
        }
        if (now < entry.staleUntil) {
          // Stale-while-revalidate: return stale data, refresh in background
          this._revalidateInBackground(functionName, body, key, ttl, options);
          return { data: entry.data as T };
        }
        // Fully expired — remove and fetch fresh
        cache.delete(key);
      }
    }

    // forceFresh also drops any existing cached value so subsequent reads get the new data
    if (forceFresh && key) cache.delete(key);

    // Dedup: return existing in-flight promise if key is non-empty
    if (key && activeRequests.has(key)) {
      return activeRequests.get(key)!;
    }

    const promise = callFunctionWithResilience<T>(functionName, body, options)
      .then((result) => {
        // Cache successful responses only
        if (key && ttl && result.data != null && !result.error) {
          cache.set(key, {
            data: result.data,
            expires: Date.now() + ttl.freshMs,
            staleUntil: Date.now() + ttl.freshMs + ttl.staleMs,
          });
        }
        return result;
      })
      .finally(() => {
        if (key) activeRequests.delete(key);
      });

    if (key) activeRequests.set(key, promise);
    return promise;
  },

  /**
   * Background revalidation for SWR — never throws, never blocks UI.
   */
  _revalidateInBackground(
    functionName: string,
    body: any,
    key: string,
    ttl: { freshMs: number; staleMs: number },
    options?: ResilienceOptions
  ): void {
    // Skip if already revalidating (dedup map covers this)
    if (activeRequests.has(key + '::revalidate')) return;

    const revalPromise = callFunctionWithResilience(functionName, body, options)
      .then((result) => {
        if (result.data != null && !result.error) {
          cache.set(key, {
            data: result.data,
            expires: Date.now() + ttl.freshMs,
            staleUntil: Date.now() + ttl.freshMs + ttl.staleMs,
          });
        }
      })
      .catch(() => {
        // Swallow — stale data remains in cache
      })
      .finally(() => {
        activeRequests.delete(key + '::revalidate');
      });

    activeRequests.set(key + '::revalidate', revalPromise);
  },

  /**
   * Prefetch an edge function into cache without returning data.
   * Uses requestIdleCallback to avoid blocking UI.
   * Safe to call multiple times — dedup prevents duplicate fetches.
   */
  prefetch(functionName: string, body: any, options?: ResilienceOptions): void {
    const key = dedupeKey(functionName, body);
    const ttl = getCacheTTL(functionName);

    // Skip if not cacheable, already cached/fresh, or already in-flight
    if (!key || !ttl) return;
    const entry = cache.get(key);
    if (entry && Date.now() < entry.expires) return;
    if (activeRequests.has(key)) return;

    const doFetch = () => {
      this.invoke(functionName, body, options).catch(() => {
        // Swallow — prefetch is best-effort
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(doFetch, { timeout: 3000 });
    } else {
      setTimeout(doFetch, 100);
    }
  },

  /**
   * Fire-and-forget: for notifications, telemetry, emails.
   * Never throws, never blocks UI.
   */
  fireAndForget(functionName: string, body: any): void {
    invokeFireAndForget(functionName, body);
  },

  /**
   * Synchronously read a cached response without triggering a network call.
   * Returns the cached data if it's still within the stale-while-revalidate
   * window (so callers can paint instantly), otherwise null.
   *
   * Use this from useState initializers to skip skeleton loaders on
   * back-navigation when we know the data is already in memory.
   */
  peekCache<T = any>(functionName: string, body: any): T | null {
    const key = dedupeKey(functionName, body);
    const ttl = getCacheTTL(functionName);
    if (!key || !ttl) return null;
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() < entry.staleUntil) {
      return entry.data as T;
    }
    return null;
  },

  /**
   * Clear all cached entries (for testing or manual purge).
   */
  clearCache(): void {
    cache.clear();
  },

  /**
   * Invalidate a single cache entry (functionName + body). Used when the
   * caller knows the cached value is no longer trustworthy (e.g. a supplier
   * just told us a rate is unavailable, so the rates list must be refetched).
   */
  invalidate(functionName: string, body: any): void {
    const key = dedupeKey(functionName, body);
    if (key) cache.delete(key);
  },

  /**
   * Get cache stats for debugging.
   */
  getCacheStats(): { size: number; keys: string[] } {
    return { size: cache.size, keys: Array.from(cache.keys()) };
  },
};

// =====================================================================
// STATIC HOTEL CONTENT CACHE (separate from the request-keyed `cache`)
// ---------------------------------------------------------------------
// Two-layer caching strategy for the hotel-details page:
//
//   LAYER 1 (this cache) — Static hotel content keyed by hotelId only.
//     Safe to cache for 2 hours. Lets us paint the property name, photos
//     and amenities the instant the user opens the page on a return
//     visit, even from a fresh page load with completely different
//     dates / guests. Survives across full-page refreshes within the
//     same tab session (in-memory only — never persisted to storage).
//
//   LAYER 2 (NOT here) — Rates / prices / book_hash / allotment.
//     These remain uncached: every visit goes through `apiClient.invoke`
//     to ETG via `internal-hotelpage`. `internal-hotelpage` is
//     deliberately absent from CACHE_TTL above so this contract holds.
// =====================================================================

const STATIC_HOTEL_TTL_MS = 2 * 60 * 60 * 1000;
const MAX_STATIC_HOTEL_ENTRIES = 200;

export interface StaticHotelContent {
  name: string;
  images: string[];
  description: string;
  address: string;
  location: any;
  coordinates: any;
  starRating: number;
  amenities: any[];
  amenityGroups: any;
  checkInTime: string;
  checkOutTime: string;
  policies: any;
  phone: string | null;
  email: string | null;
  metapolicyStruct: any;
  metapolicyExtraInfo: any;
  facts: any;
  rawRoomGroups: any[];
}

interface StaticHotelEntry {
  content: StaticHotelContent;
  cachedAt: number;
}

const staticHotelCache = new Map<string, StaticHotelEntry>();

export function getStaticHotelContent(hotelId: string | null | undefined): StaticHotelContent | null {
  try {
    if (!hotelId || typeof hotelId !== 'string' || hotelId.trim() === '') return null;
    const entry = staticHotelCache.get(hotelId);
    if (!entry) return null;
    if (Date.now() - entry.cachedAt > STATIC_HOTEL_TTL_MS) {
      staticHotelCache.delete(hotelId);
      return null;
    }
    return entry.content;
  } catch {
    return null;
  }
}

export function setStaticHotelContent(hotelId: string | null | undefined, content: StaticHotelContent | null | undefined): void {
  try {
    if (!hotelId || typeof hotelId !== 'string' || hotelId.trim() === '') return;
    if (!content) return;

    // LRU touch — re-insert moves key to the end of the iteration order.
    if (staticHotelCache.has(hotelId)) {
      staticHotelCache.delete(hotelId);
    } else if (staticHotelCache.size >= MAX_STATIC_HOTEL_ENTRIES) {
      // Drop the oldest entry (Map preserves insertion order).
      const firstKey = staticHotelCache.keys().next().value;
      if (firstKey) staticHotelCache.delete(firstKey);
    }
    staticHotelCache.set(hotelId, { content, cachedAt: Date.now() });
  } catch {
    /* silent — write failure must never break the page */
  }
}

export function invalidateStaticHotelContent(hotelId: string | null | undefined): void {
  try {
    if (!hotelId || typeof hotelId !== 'string' || hotelId.trim() === '') return;
    staticHotelCache.delete(hotelId);
  } catch {
    /* silent */
  }
}
